import { needsTagFolds } from '../item-tags';
import type {
  PendingStimulus,
  Plan,
  QuestionClass,
  Stimulus,
  StimulusHead,
  TickMeans,
} from './types';

/**
 * **Step 1: what arrived** (docs/RESOLUTION.md §1). The one constructor a
 * surface builds a stimulus with, the two pure predicates a caller asks BEFORE
 * it gathers a context, and the identity machinery of the gesture's key: a plan
 * id and a question id are hashes over the stimulus key plus what the plan
 * would do, which is what makes the executor's determinism check (§1 step 7)
 * mean what it says.
 */

/** A pending stimulus, with the gesture's own head. One constructor, so no
 *  surface assembles a stimulus field by field.
 *
 *  The HEAD is the caller's and wins: only the payload of the pending's kind
 *  is taken from it (`pendingOf` projects exactly those fields), so a pending
 *  that arrived from a client carrying `source`, `key`, `surface`,
 *  `deviceTime` or `itemId` cannot overwrite what the server stamped - the
 *  source ends up in `events.origin`. Pure. */
export function stimulusOf(pending: PendingStimulus, itemId: string, head: StimulusHead): Stimulus {
  const payload = pendingOf({ ...pending, itemId, key: '', source: 'person', surface: '' });
  return { ...payload, ...head, itemId } as Stimulus;
}

/**
 * The inverse of `stimulusOf`: a stimulus with its head and its item taken off
 * - what a proposal stores (§1 step 5), because accepting it is a NEW gesture
 * with its own key, source and time (§4: an answer is a new stimulus, never a
 * release of the stored plan). Pure.
 */
export function pendingOf(stimulus: Stimulus): PendingStimulus {
  switch (stimulus.kind) {
    case 'tick':
      return { kind: 'tick' };
    case 'untick':
      return stimulus.originKey === undefined
        ? { kind: 'untick' }
        : { kind: 'untick', originKey: stimulus.originKey };
    case 'act':
      return { kind: 'act', target: stimulus.target };
    case 'cancel':
      return stimulus.reason === undefined
        ? { kind: 'cancel' }
        : { kind: 'cancel', reason: stimulus.reason };
    case 'edge':
      return {
        kind: 'edge',
        edge: stimulus.edge,
        ...(stimulus.note === undefined ? {} : { note: stimulus.note }),
        ...(stimulus.reason === undefined ? {} : { reason: stimulus.reason }),
      };
    case 'move':
      return { kind: 'move', flowId: stimulus.flowId, toStageKey: stimulus.toStageKey };
    case 'retitle':
      return { kind: 'retitle', text: stimulus.text };
    case 'retag':
      return { kind: 'retag', from: stimulus.from, to: stimulus.to };
  }
}

/**
 * Whether this stimulus could CONCLUDE the item, and therefore whether a
 * context builder must gather the open subtree for §6's question. Pure, so
 * both substrates ask it rather than each deciding for itself when to walk.
 */
export function mayConclude(stimulus: Stimulus, grammar?: { tickMeans?: TickMeans }): boolean {
  switch (stimulus.kind) {
    case 'cancel':
      return true;
    case 'tick':
      // A tick is the forward edge (§2, 2026-11-03), and Done and Accept close
      // and sweep what is the person's alone (§6) - so the subtree is needed
      // for every tick, not only a view whose grammar says `close`. Which edge
      // it is stays step 4's question; this one is deliberately conservative,
      // because gathering too much only costs a read.
      return grammar?.tickMeans !== 'deliver' && grammar?.tickMeans !== 'accept';
    case 'edge':
      return stimulus.edge === 'done' || stimulus.edge === 'accept';
    case 'act':
      return 'command' in stimulus.target
        ? stimulus.target.command.type === 'Close'
        : stimulus.target.attribute !== 'active';
    case 'move':
      // A stage's `means` may carry `closed: true`; which stage, and what it
      // applies, is the context builder's own gather.
      return true;
    default:
      return false;
  }
}

/**
 * Whether the context builder must load the item's worn tags
 * (`WorkItemState.tagFolds`) for this stimulus: an act applying or removing a
 * tag, and an un-tick, whose inverse may be one (a retag's plan is taken back
 * through it). Pure, so both substrates ask it rather than each deciding.
 */
export function needsItemTags(stimulus: Stimulus): boolean {
  switch (stimulus.kind) {
    case 'act':
      return 'command' in stimulus.target && needsTagFolds(stimulus.target.command);
    case 'untick':
    case 'retitle':
    case 'retag':
      return true;
    default:
      return false;
  }
}

/**
 * Which stage a stimulus could draw the member into, by what it would land
 * on: a tick or an edge that hands work in looks for a delivery stage, one on
 * an item already in review (the reviewer's Accept) for the acceptance stage.
 * Null for a stimulus that moves no position. Pure; both substrates ask it (`deliveryTargetFor`, src/read/resolution-context.ts).
 */
export function targetTowardOf(
  stimulus: Stimulus,
  item: { delivered: boolean; closed: boolean },
): 'delivery' | 'acceptance' | null {
  const inReview = item.delivered && !item.closed;
  if (stimulus.kind === 'tick') return inReview ? 'acceptance' : 'delivery';
  if (stimulus.kind === 'edge') {
    if (stimulus.edge === 'accept') return 'acceptance';
    return stimulus.edge === 'done' || stimulus.edge === 'ready' ? 'delivery' : null;
  }
  if (stimulus.kind === 'act' && 'attribute' in stimulus.target) {
    const { attribute } = stimulus.target;
    if (attribute === 'active') return null;
    return inReview ? 'acceptance' : 'delivery';
  }
  return null;
}

/** Stable JSON: object keys sorted, so a hash over a value does not depend on
 *  the order its literal was written in. */
function stableStringify(value: unknown): string {
  if (value === undefined) return 'null';
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`).join(',')}}`;
}

/** A small, dependency-free 64-bit-ish hash (two FNV-1a-style lanes). The
 *  domain runs in the browser too (the strike waits on nothing, §10), so
 *  `node:crypto` is not available here; collision resistance is not a security
 *  property of this id, only determinism is. */
function digest(input: string): string {
  let a = 0x811c9dc5;
  let b = 0x9e3779b9;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b + c, 0x85ebca6b) >>> 0;
    b = ((b << 13) | (b >>> 19)) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

export function planId(
  stimulusKey: string,
  body: Omit<Plan, 'id' | 'statement' | 'intent'>,
): string {
  return digest(
    stableStringify({
      key: stimulusKey,
      steps: body.steps,
      transition: body.transition,
    }),
  );
}

export function questionId(stimulusKey: string, klass: QuestionClass): string {
  return digest(stableStringify({ key: stimulusKey, class: klass }));
}
